import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useChatPanel } from '../context/ChatPanelContext';
import {
  readDeepLinkFromUrl,
  consumeDeepLink,
  stripDeepLinkFromUrl,
  type EmailDeepLinkParams,
} from '../utils/emailDeepLink';

/**
 * Consume an email deep-link (?prompt=...&autoSend=1) once the user is
 * authenticated. Reads from URL first, then falls back to a sessionStorage
 * stash that AuthContext sets before the Federate redirect.
 *
 * Triggered exactly once per mount via firedRef + URL strip + get-and-delete
 * stash, so refresh / back-nav / strict-mode re-entry never re-sends.
 *
 * `enabled` gates whether we actually fire the deep-link. ChatShell passes
 * false when the current surface would deny chat (e.g. a rocky-denied user
 * lands on `/` while an email deep-link is in the URL) — otherwise we'd
 * silently auto-send a prompt for an unauthorized user.
 */
export function useEmailDeepLink(enabled: boolean = true) {
  const { isAuthenticated } = useAuth();
  const { sendPrompt, prefillPrompt } = useChatPanel();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !enabled || firedRef.current) return;

    let dl: EmailDeepLinkParams | null = readDeepLinkFromUrl();
    if (!dl) dl = consumeDeepLink();
    if (!dl) return;

    firedRef.current = true;
    stripDeepLinkFromUrl();

    if (dl.autoSend) {
      sendPrompt(dl.prompt);
    } else {
      prefillPrompt(dl.prompt);
    }

    if (dl.source) {
      console.log('[deepLink] consumed from', dl.source);
    }
  }, [isAuthenticated, enabled, sendPrompt, prefillPrompt]);
}
